import * as React from "react";

import { AccessReportsService } from "@app/access-reports";
import { AccessReportsAdminLogo, ManageOptions } from "@app/access-reports/admin";
import { AccessReportsWizardLogo } from "@app/access-reports/shared";
import { DragAndDropFileUploader, LoadingBox, LoadingSpinner } from "@app/shared";
import { cloneDeep, isEqual } from "lodash";
import { QESCheckbox } from "../../shared/qes-standards/forms/Checkbox";
import { JSX } from "react";
//import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";

interface ManageLogosProps {
  /**
   * Prevents the user from making modifications to the template
   */
  disableUserActions: boolean;
  /**
   * The list of options to be managed
   */
  logos: AccessReportsWizardLogo[];
  /**
   * Handles saving any modifications done to the list of options
   */
  onSaveLogosClick: (options: AccessReportsWizardLogo[], reloadPage: boolean) => void;
}

interface ManageLogosState {
  loading: boolean;
  logoBlobs: Map<number, Blob>;
  logos: AccessReportsWizardLogo[];
}

class ManageLogo extends React.Component<ManageLogosProps, ManageLogosState> {
  private readonly accessReportsService = new AccessReportsService();

  constructor(props: ManageLogosProps) {
    super(props);

    this.state = {
      loading: false,
      logoBlobs: new Map<number, Blob>(),
      logos: [],
    };
  }

  public async componentDidMount() {
    if (this.props.logos.length) {
      this.setLogos();
      await this.getLogoBlobs();
    }
  }

  public async componentDidUpdate(prevProps: ManageLogosProps) {
    if (!isEqual(prevProps.logos, this.props.logos)) {
      this.setLogos();
      await this.getLogoBlobs();
    }
  }

  public render() {
    return (
      <React.Fragment>
        <ManageOptions
          buildAdditionalIndividualProperty={this.buildAdditionalIndividualProperty}
          additionalProperties={!this.props.disableUserActions && this.getAdditionalProperties()}
          disableAddingNewOptions={true}
          disableUserActions={this.props.disableUserActions}
          onSaveOptionsClick={this.onSaveLogosClick}
          options={this.state.logos}
          optionType={"logo"}
          savedOptions={this.props.logos}
        />
      </React.Fragment>
    );
  }

  private buildAdditionalIndividualProperty = (logo: AccessReportsWizardLogo): JSX.Element => {
    const logoBlob: Blob = this.state.logoBlobs.get(logo.id);

    return (
      <React.Fragment>
        <div className="row">
          <div className="col-12">
            <div className="mt-3">
              {!!logoBlob ? (
                <img className="access-reports-admin__manage-logo--preview" src={URL.createObjectURL(logoBlob)} />
              ) : (
                <LoadingSpinner />
              )}
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-12">
            <QESCheckbox
              containerClassName="mt-3 mb-3"
              checked={logo.maintainAspectRatio}
              disabled={this.props.disableUserActions}
              label="Maintain Aspect Ratio"
              testId={`access-reports-admin__form--maintain-aspect-ratio-${logo.id}`}
              onChange={() => this.onCheckboxChange(logo.id, "maintainAspectRatio")}
            />
          </div>
        </div>

        <div className="row">
          <div className="col-12">
            <QESCheckbox
              containerClassName="mt-0 mb-3"
              checked={logo.stretchToFit}
              disabled={this.props.disableUserActions}
              label="Stretch to Fit"
              testId={`access-reports-admin__form--stretch-to-fit-${logo.id}`}
              onChange={() => this.onCheckboxChange(logo.id, "stretchToFit")}
            />
          </div>
        </div>
      </React.Fragment>
    );
  }

  private getAdditionalProperties = (): JSX.Element => {
    return (
      <div className="row">
        <div className="col-12">
          <div id="access-reports-admin__cover-page--loading-box">
            <LoadingBox show={this.state.loading} />
          </div>

          <DragAndDropFileUploader
            apiKeyName="logoFile"
            title=""
            filetype=".jpeg,.png"
            onUpload={this.onUpload}
            small={true}
            testId="access-reports-admin__cover-page--file-upload"
          />
          <hr className="mt-0" />
        </div>
      </div>
    );
  }

  private setLogos = (): void => {
    // retrieve the logo images
    if (this.props.logos.length) {
      // the direct options prop array is not modified
      // in case the user wishes to "cancel" any changes made
      this.setState({
        logos: cloneDeep(this.props.logos),
      });
    }
  }

  private getLogoBlobs = async (): Promise<void> => {
    const logoBlobs: Map<number, Blob> = new Map<number, Blob>();

    for (const logo of this.props.logos) {
      const logoBlob: Blob = await this.accessReportsService.getLogoById(logo.id);

      if (!!logoBlob) {
        logoBlobs.set(logo.id, logoBlob);
      }
    }

    this.setState({
      logoBlobs,
    });
  }

  public onCheckboxChange = (optionId: number, key: keyof AccessReportsWizardLogo): void => {
    const logos: AccessReportsWizardLogo[] = cloneDeep(this.state.logos);
    const logo: AccessReportsWizardLogo = logos.find(x => x.id === optionId);

    if (!!logo) {
      logo[key] = !logo[key];

      this.setState({
        logos,
      });
    }
  }

  public onSaveLogosClick = (logos: AccessReportsWizardLogo[]): void => {
    this.props.onSaveLogosClick(logos, true)
  }

  public onUpload = async (data: FormData): Promise<void> => {
    return new Promise<void>((resolve, reject) => {
      const logoBlobs: Map<number, Blob> = new Map(this.state.logoBlobs);
      const logos: AccessReportsWizardLogo[] = cloneDeep(this.state.logos);

      this.setState({
        loading: true,
      }, async () => {

        try {
          const logoProperties: AccessReportsAdminLogo = await this.accessReportsService.uploadLogo(data);

          if (!!logoProperties) {
            const logoOption: AccessReportsWizardLogo = {
              defaultProducts: [],
              displayName: logoProperties.imageName,
              hiddenProducts: [],
              id: logoProperties.accessLogoId,
              logoPath: "",
              maintainAspectRatio: true,
              name: logoProperties.imageName,
              stretchToFit: true,
            }

            logos.push(logoOption);

            const logoBlob: Blob = await this.accessReportsService.getLogoById(logoOption.id);

            if (!!logoBlob) {
              logoBlobs.set(logoOption.id, logoBlob);
            }

            await this.props.onSaveLogosClick(logos, false);
          }

          resolve();
        } catch {
          reject();
        }

        this.setState({
          loading: false,
          logoBlobs,
          logos,
        });
      });
    });
  }
}

export { ManageLogo };
